import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { toast } from 'sonner';
import {
  Loader2, RefreshCw, Search, ChevronLeft, ChevronRight, X,
  CheckCircle2, XCircle, Trash2, Undo2, History, AlertOctagon,
} from 'lucide-react';
import { getApiBase } from '../../api/httpClient';
import WasslaImage from '../../components/common/WasslaImage';
import './AdminLayout.css';
import './AdminAds.css';

const STATUS_LABELS_EN = {
  draft: 'Draft',
  pending_review: 'Pending Review',
  active: 'Active',
  hidden_by_user: 'Hidden by user',
  sold: 'Sold',
  rejected: 'Rejected',
  hidden_by_reports: 'Hidden (reports)',
  removed_by_moderation: 'Removed',
  needs_photo_reupload: 'Needs Photo Reupload',
};
const STATUS_LABELS_AR = {
  draft: 'مسودة',
  pending_review: 'قيد المراجعة',
  active: 'نشط',
  hidden_by_user: 'مخفي بواسطة المالك',
  sold: 'مباع',
  rejected: 'مرفوض',
  hidden_by_reports: 'مخفي (بلاغات)',
  removed_by_moderation: 'مزال',
  needs_photo_reupload: 'بحاجة لإعادة رفع الصورة',
};
const STATUS_PILL = {
  active: 'green',
  pending_review: 'yellow',
  rejected: 'red',
  removed_by_moderation: 'red',
  hidden_by_reports: 'red',
  hidden_by_user: 'gray',
  sold: 'blue',
  draft: 'gray',
  // iter278 — neutral cleanup state (NOT a moderation removal). Yellow
  // = "seller can re-edit and re-upload to bring the ad back".
  needs_photo_reupload: 'yellow',
};

// iter289 — Tabs are split into two groups:
//   * MARKETPLACE_STATUSES — real marketplace ad statuses. These show
//     in the main tab row, the test/seed user filter is applied, and
//     the tab counts match the actual filtered list.
//   * LEGACY_STATUSES — `needs_photo_reupload` repair queue. Has its
//     own pill (yellow, off to the right) so it's never confused with
//     real marketplace inventory.
const MARKETPLACE_STATUSES = [
  'draft',
  'pending_review',
  'active',
  'hidden_by_user',
  'sold',
  'rejected',
  'hidden_by_reports',
  'removed_by_moderation',
];
const LEGACY_STATUSES = ['needs_photo_reupload'];
const ALL_STATUSES = [...MARKETPLACE_STATUSES, ...LEGACY_STATUSES];
const PAGE_SIZE = 25;

export default function AdminAds() {
  const { token } = useAuth();
  const { lang } = useLanguage();
  const location = useLocation();
  const isArabic = lang === 'ar';
  const labels = isArabic ? STATUS_LABELS_AR : STATUS_LABELS_EN;

  // Read initial status filter from URL search params so Premium Overview KPI
  // tiles can deep-link here (e.g. /admin/ads?status=pending_review). The
  // selected pill highlights directly above the table — no sideways drift.
  const initialStatusFromUrl = (() => {
    const search = location.search || '';
    const hash = location.hash || '';
    // HashRouter sometimes puts the query after the hash route fragment.
    const qIdx = hash.indexOf('?');
    const params = new URLSearchParams(qIdx >= 0 ? hash.slice(qIdx + 1) : search.replace(/^\?/, ''));
    const s = params.get('status') || '';
    return ALL_STATUSES.includes(s) ? s : '';
  })();

  const [status, setStatus] = useState(initialStatusFromUrl);

  // If the user re-navigates to /admin/ads?status=... while already on the
  // page (e.g. clicking a different KPI tile in another tab), keep the
  // status filter in sync with the URL.
  useEffect(() => {
    const hash = location.hash || '';
    const qIdx = hash.indexOf('?');
    const params = new URLSearchParams(
      qIdx >= 0 ? hash.slice(qIdx + 1) : (location.search || '').replace(/^\?/, '')
    );
    const s = params.get('status') || '';
    if (ALL_STATUSES.includes(s) && s !== status) {
      setStatus(s);
      setSkip(0);
    } else if (s === '' && status !== '' && params.has('status') === false) {
      // No status param present at all → don't auto-clear (user may have
      // landed here without a deep-link). This branch is intentionally a
      // no-op.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search, location.hash]);
  const [q, setQ] = useState('');
  const [qInput, setQInput] = useState('');
  const [skip, setSkip] = useState(0);
  const [data, setData] = useState({ items: [], total: 0, diagnostic: null });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(new Set());

  // Action modal state
  const [modal, setModal] = useState(null);   // { action, ids, requiresReason }
  const [reason, setReason] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // iter267: diagnostic error state (mirrors AdminOverview from iter263)
  // — never silently show 0 if the API call failed.
  const [error, setError] = useState(null);

  // Audit drawer state
  const [auditFor, setAuditFor] = useState(null);    // ad_id
  const [auditItems, setAuditItems] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  const load = useCallback(async () => {
    // Same defence-in-depth as AdminOverview: refuse to send "Bearer null".
    const rawToken = token || (typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null);
    const liveToken =
      rawToken && rawToken !== 'null' && rawToken !== 'undefined'
        ? String(rawToken).trim()
        : null;
    if (!liveToken) {
      setError({
        endpoint: '/api/admin/dashboard/ads',
        status: 401,
        message: isArabic
          ? 'انتهت الجلسة. الرجاء تسجيل الدخول مرة أخرى.'
          : 'Your session has expired. Please sign in again.',
      });
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (status) params.set('status', status);
      if (q) params.set('q', q);
      params.set('skip', String(skip));
      params.set('limit', String(PAGE_SIZE));
      // Smart base — same-origin on production custom domains so CORS
      // does not silently turn every admin fetch into "Failed to fetch".
      const apiBase = getApiBase();
      const path = `/api/admin/dashboard/ads?${params.toString()}`;
      const resp = await fetch(`${apiBase}${path}`, {
        headers: { Authorization: `Bearer ${liveToken}` },
        credentials: 'include',
      });
      if (!resp.ok) {
        let bodyMsg = '';
        try {
          const j = await resp.json();
          bodyMsg = j?.detail || JSON.stringify(j).slice(0, 200);
        } catch (_e) {
          bodyMsg = (await resp.text()).slice(0, 200);
        }
        setError({
          endpoint: '/api/admin/dashboard/ads',
          status: resp.status,
          message: bodyMsg || resp.statusText || 'No detail',
        });
        toast.error(
          isArabic
            ? `فشل تحميل الإعلانات (${resp.status})`
            : `Failed to load ads (${resp.status})`
        );
        return;
      }
      const json = await resp.json();
      setData(json);
      setSelected(new Set());
    } catch (e) {
      setError({
        endpoint: '/api/admin/dashboard/ads',
        status: 0,
        message: (e && e.message) ? e.message : String(e),
      });
      toast.error(isArabic ? 'تعذر الاتصال بالخادم' : 'Network error contacting the server');
    } finally {
      setLoading(false);
    }
  }, [token, status, q, skip, isArabic]);

  useEffect(() => { load(); }, [load]);

  const pages = useMemo(() => Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE)), [data.total]);
  const currentPage = Math.floor(skip / PAGE_SIZE) + 1;

  const toggleSelectAll = () => {
    if (selected.size === data.items.length && data.items.length > 0) {
      setSelected(new Set());
    } else {
      setSelected(new Set(data.items.map((it) => it.ad_id)));
    }
  };

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const openAction = (action, ids) => {
    if (!ids || ids.length === 0) return;
    const requiresReason = action === 'reject' || action === 'remove';
    setReason('');
    setNote('');
    setModal({ action, ids, requiresReason });
  };

  const closeModal = () => { if (!submitting) setModal(null); };

  const submitAction = async () => {
    if (!modal) return;
    if (modal.requiresReason && !reason.trim()) {
      toast.error(isArabic ? 'السبب مطلوب' : 'A reason is required');
      return;
    }
    setSubmitting(true);
    try {
      if (modal.ids.length === 1) {
        const r = await fetch(
          `${getApiBase()}/api/admin/dashboard/ads/${modal.ids[0]}/action`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ action: modal.action, reason, note }),
          }
        );
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          toast.error(err.detail || (isArabic ? 'فشل الإجراء' : 'Action failed'));
        } else {
          toast.success(isArabic ? 'تم تنفيذ الإجراء' : 'Action completed');
        }
      } else {
        const r = await fetch(
          `${getApiBase()}/api/admin/dashboard/ads/bulk`,
          {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
            body: JSON.stringify({ ad_ids: modal.ids, action: modal.action, reason, note }),
          }
        );
        if (!r.ok) {
          const err = await r.json().catch(() => ({}));
          toast.error(err.detail || (isArabic ? 'فشل الإجراء' : 'Action failed'));
        } else {
          const j = await r.json();
          toast.success(
            isArabic
              ? `نجاح: ${j.success_count} · فشل: ${j.failure_count}`
              : `Success: ${j.success_count} · Failed: ${j.failure_count}`
          );
        }
      }
      setModal(null);
      await load();
    } catch (e) {
      toast.error(isArabic ? 'حدث خطأ' : 'Something went wrong');
    } finally {
      setSubmitting(false);
    }
  };

  const openAudit = async (adId) => {
    setAuditFor(adId);
    setAuditLoading(true);
    setAuditItems([]);
    try {
      const r = await fetch(
        `${getApiBase()}/api/admin/dashboard/ads/${adId}/audit`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (r.ok) {
        const j = await r.json();
        setAuditItems(j.items || []);
      }
    } finally {
      setAuditLoading(false);
    }
  };

  return (
    <div data-testid="admin-ads">
      <div className="admin-ads-header">
        <div>
          <h1 className="admin-ads-title" data-testid="admin-ads-title">
            {isArabic ? 'إدارة الإعلانات' : 'Ads Management'}
          </h1>
          <p className="admin-ads-subtitle">
            {isArabic
              ? 'إعلانات السوق الحقيقية فقط. الأرقام الرئيسية تستثني السجلات التجريبية والبذرة والمحذوفة وطابور إصلاح الصور القديمة.'
              : 'Real marketplace ads only. Primary numbers exclude test, seed, deleted, and the legacy image repair queue.'}
          </p>
        </div>
        <button type="button" className="admin-btn" onClick={load} data-testid="admin-ads-refresh">
          <RefreshCw size={14} />
          {isArabic ? 'تحديث' : 'Refresh'}
        </button>
      </div>

      <div className="admin-ads-filters">
        {(status || q) && (
          <div className="admin-ads-active-chips" data-testid="admin-ads-active-chips">
            <span className="admin-ads-active-chips-label">
              {isArabic ? 'مرشحات نشطة:' : 'Active filters:'}
            </span>
            {status && (
              <button
                type="button"
                className={`admin-ads-chip ${STATUS_PILL[status] || 'gray'}`}
                onClick={() => { setStatus(''); setSkip(0); }}
                data-testid="admin-ads-chip-status"
                aria-label={
                  isArabic
                    ? `إزالة مرشح ${labels[status]}`
                    : `Remove filter ${labels[status]}`
                }
              >
                <span>{labels[status]}</span>
                <X size={12} />
              </button>
            )}
            {q && (
              <button
                type="button"
                className="admin-ads-chip gray"
                onClick={() => { setQInput(''); setQ(''); setSkip(0); }}
                data-testid="admin-ads-chip-query"
                aria-label={isArabic ? 'إزالة البحث' : 'Clear search'}
              >
                <span>"{q}"</span>
                <X size={12} />
              </button>
            )}
            <button
              type="button"
              className="admin-ads-chip-clear"
              onClick={() => { setStatus(''); setQInput(''); setQ(''); setSkip(0); }}
              data-testid="admin-ads-chips-clear-all"
            >
              {isArabic ? 'مسح الكل' : 'Clear all'}
            </button>
          </div>
        )}
        <div className="admin-ads-search">
          <Search size={14} />
          <input
            type="text"
            value={qInput}
            placeholder={isArabic ? 'بحث في العنوان، الوصف، أو المعرّف…' : 'Search title, description, ad_id…'}
            onChange={(e) => setQInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') { setSkip(0); setQ(qInput); } }}
            data-testid="admin-ads-search-input"
          />
          {qInput && (
            <button type="button" className="admin-btn ghost" onClick={() => { setQInput(''); setQ(''); setSkip(0); }} data-testid="admin-ads-search-clear">
              <X size={14} />
            </button>
          )}
        </div>
        <div className="admin-ads-status-filters" role="tablist" data-testid="admin-ads-status-filters">
          <button
            type="button"
            className={`admin-pill ${status === '' ? 'blue' : 'gray'}`}
            onClick={() => { setStatus(''); setSkip(0); }}
            data-testid="admin-ads-filter-all"
            data-active={status === '' ? 'true' : 'false'}
            aria-pressed={status === ''}
            role="tab"
            title={isArabic ? 'كل إعلانات السوق الحقيقية' : 'All real marketplace ads'}
          >
            {isArabic ? 'الكل' : 'All'}
          </button>
          {MARKETPLACE_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`admin-pill ${status === s ? STATUS_PILL[s] : 'gray'}`}
              onClick={() => { setStatus(s); setSkip(0); }}
              data-testid={`admin-ads-filter-${s}`}
              data-active={status === s ? 'true' : 'false'}
              aria-pressed={status === s}
              role="tab"
            >
              {labels[s]}
            </button>
          ))}
          {/* iter289 — Legacy Image Repair Queue tab is visually
              separated so admins can tell it's not part of the real
              marketplace inventory. */}
          <span style={{ width: 1, alignSelf: 'stretch', background: 'var(--admin-border)', margin: '0 4px' }} aria-hidden="true" />
          {LEGACY_STATUSES.map((s) => (
            <button
              key={s}
              type="button"
              className={`admin-pill ${status === s ? STATUS_PILL[s] : 'gray'}`}
              onClick={() => { setStatus(s); setSkip(0); }}
              data-testid={`admin-ads-filter-${s}`}
              data-active={status === s ? 'true' : 'false'}
              aria-pressed={status === s}
              role="tab"
              title={isArabic
                ? 'طابور إصلاح الصور القديمة — غير محسوب في إجمالي السوق'
                : 'Legacy Image Repair Queue — not counted in marketplace total'}
            >
              {labels[s]}
            </button>
          ))}
        </div>
      </div>

      {/* iter289 — Diagnostic ribbon above the table: matches the
          Admin Overview KPI pills exactly so the operator can confirm
          the two surfaces share a single source of truth. */}
      {data.diagnostic && (
        <div
          className="admin-card"
          data-testid="admin-ads-diagnostic"
          style={{
            display: 'flex',
            gap: 8,
            flexWrap: 'wrap',
            alignItems: 'center',
            padding: '8px 12px',
            marginBottom: 8,
            background: 'var(--admin-card-bg)',
          }}
        >
          <span className="admin-pill green" data-testid="admin-ads-diag-real">
            {isArabic ? 'سوق حقيقي' : 'Real Marketplace'}: <strong>{data.diagnostic.real_marketplace ?? 0}</strong>
          </span>
          {(data.diagnostic.legacy_repair_queue ?? 0) > 0 && (
            <span className="admin-pill yellow" data-testid="admin-ads-diag-legacy">
              {isArabic ? 'إصلاح الصور القديمة' : 'Legacy Repair'}: <strong>{data.diagnostic.legacy_repair_queue}</strong>
            </span>
          )}
          {(data.diagnostic.test_seed_owned_excluded ?? 0) > 0 && (
            <span className="admin-pill gray" data-testid="admin-ads-diag-test">
              {isArabic ? 'اختبار/بذرة' : 'Test/Seed'}: <strong>{data.diagnostic.test_seed_owned_excluded}</strong>
            </span>
          )}
          <span className="admin-pill blue" data-testid="admin-ads-diag-raw">
            {isArabic ? 'الكل (خام)' : 'Raw DB'}: <strong>{data.diagnostic.raw_db_total ?? 0}</strong>
          </span>
          <span style={{ marginInlineStart: 'auto', fontSize: 11, color: 'var(--admin-text-dim)' }}>
            {isArabic
              ? 'الأرقام الرئيسية تستثني السجلات التجريبية والبذرة والمحذوفة.'
              : 'Primary numbers exclude test, seed, deleted, and system records.'}
          </span>
        </div>
      )}

      {selected.size > 0 && (
        <div className="admin-ads-bulk-bar" data-testid="admin-ads-bulk-bar">
          <span>
            {isArabic ? `محدد: ${selected.size}` : `${selected.size} selected`}
          </span>
          <div className="admin-ads-bulk-actions">
            <button type="button" className="admin-btn primary" onClick={() => openAction('approve', Array.from(selected))} data-testid="admin-ads-bulk-approve">
              <CheckCircle2 size={14} />{isArabic ? 'موافقة' : 'Approve'}
            </button>
            <button type="button" className="admin-btn warn" onClick={() => openAction('reject', Array.from(selected))} data-testid="admin-ads-bulk-reject">
              <XCircle size={14} />{isArabic ? 'رفض' : 'Reject'}
            </button>
            <button type="button" className="admin-btn danger" onClick={() => openAction('remove', Array.from(selected))} data-testid="admin-ads-bulk-remove">
              <Trash2 size={14} />{isArabic ? 'إزالة' : 'Remove'}
            </button>
            <button type="button" className="admin-btn" onClick={() => openAction('restore', Array.from(selected))} data-testid="admin-ads-bulk-restore">
              <Undo2 size={14} />{isArabic ? 'استعادة' : 'Restore'}
            </button>
            <button type="button" className="admin-btn ghost" onClick={() => setSelected(new Set())} data-testid="admin-ads-bulk-clear">
              <X size={14} />{isArabic ? 'إلغاء' : 'Clear'}
            </button>
          </div>
        </div>
      )}

      <div className="admin-card" style={{ padding: 0, overflow: 'hidden' }}>
        {error && (
          <div
            className="admin-card"
            data-testid="admin-ads-error"
            style={{
              margin: 12,
              borderInlineStart: '3px solid #ef4444',
              background: 'rgba(239, 68, 68, 0.06)',
            }}
          >
            <h4
              style={{
                color: '#ef4444',
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                margin: 0,
              }}
            >
              <AlertOctagon size={14} />
              {isArabic ? 'تعذر تحميل الإعلانات' : 'Failed to load ads'}
            </h4>
            <ul
              style={{ fontSize: 12, lineHeight: 1.7, marginTop: 8, paddingInlineStart: 18 }}
            >
              <li>
                {isArabic ? 'النقطة' : 'Endpoint'}:{' '}
                <code data-testid="admin-ads-error-endpoint">{error.endpoint}</code>
              </li>
              <li>
                {isArabic ? 'حالة HTTP' : 'HTTP status'}:{' '}
                <strong data-testid="admin-ads-error-status">{error.status}</strong>
              </li>
              <li>
                {isArabic ? 'التفاصيل' : 'Detail'}:{' '}
                <span data-testid="admin-ads-error-detail">{error.message}</span>
              </li>
            </ul>
            <button
              type="button"
              className="admin-btn"
              onClick={load}
              style={{ marginTop: 6 }}
              data-testid="admin-ads-error-retry"
            >
              <RefreshCw size={14} />
              {isArabic ? 'إعادة المحاولة' : 'Retry'}
            </button>
          </div>
        )}
        <div className="admin-ads-table-wrap">
          <table className="admin-ads-table" data-testid="admin-ads-table">
            <thead>
              <tr>
                <th style={{ width: 36 }}>
                  <input
                    type="checkbox"
                    checked={data.items.length > 0 && selected.size === data.items.length}
                    onChange={toggleSelectAll}
                    data-testid="admin-ads-select-all"
                  />
                </th>
                <th>{isArabic ? 'الإعلان' : 'Ad'}</th>
                <th>{isArabic ? 'المالك' : 'Owner'}</th>
                <th>{isArabic ? 'الحالة' : 'Status'}</th>
                <th>{isArabic ? 'تم إنشاؤه' : 'Created'}</th>
                <th style={{ textAlign: 'end' }}>{isArabic ? 'إجراءات' : 'Actions'}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center' }}>
                  <Loader2 className="animate-spin" size={16} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}
                </td></tr>
              )}
              {!loading && data.items.length === 0 && (
                <tr><td colSpan={6} style={{ padding: 24, textAlign: 'center', color: 'var(--admin-text-dim)' }} data-testid="admin-ads-empty">
                  {isArabic ? 'لا توجد إعلانات.' : 'No ads match the filters.'}
                </td></tr>
              )}
              {!loading && data.items.map((ad) => (
                <tr key={ad.ad_id} data-testid={`admin-ads-row-${ad.ad_id}`}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selected.has(ad.ad_id)}
                      onChange={() => toggleRow(ad.ad_id)}
                      data-testid={`admin-ads-select-${ad.ad_id}`}
                    />
                  </td>
                  <td>
                    <div className="admin-ads-ad-cell">
                      {/* iter327 — WasslaImage at admin thumb size
                          (44×44). Caption suppressed because the cell
                          is too tight; operators rely on the dimmed
                          mark to spot a failed image. */}
                      {ad.thumbnail ? (
                        <div className="admin-ads-thumb admin-ads-thumb--branded">
                          <WasslaImage
                            src={ad.thumbnail}
                            alt=""
                            fallbackType="ad"
                            testid={`admin-ads-thumb-${ad.ad_id}`}
                            showCaption={false}
                          />
                        </div>
                      ) : (
                        <div className="admin-ads-thumb placeholder" />
                      )}
                      <div className="admin-ads-ad-meta">
                        <div className="admin-ads-ad-title" title={ad.title}>{ad.title || '—'}</div>
                        <div className="admin-ads-ad-id">{ad.ad_id}</div>
                        {/* iter328 — Admin-visible delivery hint so
                            misleading delivery claims can be spotted
                            without opening the full ad. */}
                        {ad.delivery_available && (
                          <div
                            className="admin-ads-delivery-hint"
                            data-testid={`admin-ads-delivery-${ad.ad_id}`}
                            title={ad.delivery_area || ''}
                          >
                            🚚 {isArabic ? 'توصيل' : 'Delivery'}
                            {ad.delivery_type === 'free' && ` · ${isArabic ? 'مجاني' : 'free'}`}
                            {ad.delivery_type === 'paid' && ad.delivery_fee_amount != null && (
                              ` · ${ad.delivery_fee_currency || 'SDG'} ${ad.delivery_fee_amount}`
                            )}
                            {ad.delivery_type === 'depends_on_location' && (
                              ` · ${isArabic ? 'حسب الموقع' : 'by location'}`
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <div className="admin-ads-owner">
                      <div>{ad.owner?.name || '—'}</div>
                      <div className="admin-ads-owner-email">{ad.owner?.email || ''}</div>
                    </div>
                  </td>
                  <td>
                    <span className={`admin-pill ${STATUS_PILL[ad.status] || 'gray'}`} data-testid={`admin-ads-status-${ad.ad_id}`}>
                      {labels[ad.status] || ad.status}
                    </span>
                  </td>
                  <td className="admin-ads-date">
                    {ad.created_at ? new Date(ad.created_at).toLocaleDateString() : '—'}
                  </td>
                  <td>
                    <div className="admin-ads-actions">
                      <button type="button" className="admin-btn primary" title={isArabic ? 'موافقة' : 'Approve'} onClick={() => openAction('approve', [ad.ad_id])} data-testid={`admin-ads-approve-${ad.ad_id}`}>
                        <CheckCircle2 size={14} />
                      </button>
                      <button type="button" className="admin-btn warn" title={isArabic ? 'رفض' : 'Reject'} onClick={() => openAction('reject', [ad.ad_id])} data-testid={`admin-ads-reject-${ad.ad_id}`}>
                        <XCircle size={14} />
                      </button>
                      <button type="button" className="admin-btn danger" title={isArabic ? 'إزالة' : 'Remove'} onClick={() => openAction('remove', [ad.ad_id])} data-testid={`admin-ads-remove-${ad.ad_id}`}>
                        <Trash2 size={14} />
                      </button>
                      <button type="button" className="admin-btn" title={isArabic ? 'استعادة' : 'Restore'} onClick={() => openAction('restore', [ad.ad_id])} data-testid={`admin-ads-restore-${ad.ad_id}`}>
                        <Undo2 size={14} />
                      </button>
                      <button type="button" className="admin-btn ghost" title={isArabic ? 'سجل المراجعة' : 'Audit history'} onClick={() => openAudit(ad.ad_id)} data-testid={`admin-ads-audit-${ad.ad_id}`}>
                        <History size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="admin-ads-pager">
          <span data-testid="admin-ads-total">
            {isArabic ? `الإجمالي: ${data.total}` : `Total: ${data.total}`}
          </span>
          <div className="admin-ads-pager-controls">
            <button type="button" className="admin-btn" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - PAGE_SIZE))} data-testid="admin-ads-page-prev">
              <ChevronLeft size={14} />
            </button>
            <span className="admin-ads-page-label" data-testid="admin-ads-page-label">
              {currentPage} / {pages}
            </span>
            <button type="button" className="admin-btn" disabled={skip + PAGE_SIZE >= data.total} onClick={() => setSkip(skip + PAGE_SIZE)} data-testid="admin-ads-page-next">
              <ChevronRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Confirm action modal */}
      {modal && (
        <div className="admin-modal-backdrop" onClick={closeModal} data-testid="admin-action-modal">
          <div className="admin-modal" onClick={(e) => e.stopPropagation()}>
            <h3 className="admin-modal-title">
              {isArabic ? 'تأكيد الإجراء' : 'Confirm action'}
            </h3>
            <p className="admin-modal-body">
              {isArabic
                ? `أنت على وشك تنفيذ "${modal.action}" على ${modal.ids.length} إعلان.`
                : `You are about to ${modal.action} ${modal.ids.length} ad(s).`}
            </p>
            {modal.requiresReason && (
              <div className="admin-modal-field">
                <label>{isArabic ? 'السبب (مطلوب)' : 'Reason (required)'}</label>
                <input
                  type="text"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  placeholder={isArabic ? 'مثال: مخالفة سياسة المحتوى' : 'e.g. policy_violation'}
                  data-testid="admin-action-reason"
                />
              </div>
            )}
            <div className="admin-modal-field">
              <label>{isArabic ? 'ملاحظة (اختياري)' : 'Note (optional)'}</label>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder={isArabic ? 'سياق إضافي للمشرفين' : 'Additional context for admins'}
                data-testid="admin-action-note"
              />
            </div>
            <div className="admin-modal-footer">
              <button type="button" className="admin-btn ghost" onClick={closeModal} disabled={submitting} data-testid="admin-action-cancel">
                {isArabic ? 'إلغاء' : 'Cancel'}
              </button>
              <button type="button" className="admin-btn primary" onClick={submitAction} disabled={submitting} data-testid="admin-action-confirm">
                {submitting ? <Loader2 className="animate-spin" size={14} /> : null}
                {isArabic ? 'تأكيد' : 'Confirm'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Per-ad audit drawer */}
      {auditFor && (
        <div className="admin-drawer-backdrop" onClick={() => setAuditFor(null)} data-testid="admin-audit-drawer">
          <div className={`admin-drawer ${isArabic ? 'rtl' : 'ltr'}`} onClick={(e) => e.stopPropagation()}>
            <div className="admin-drawer-header">
              <h3>{isArabic ? 'سجل المراجعة' : 'Audit history'} — {auditFor}</h3>
              <button type="button" className="admin-btn ghost" onClick={() => setAuditFor(null)} data-testid="admin-audit-close">
                <X size={14} />
              </button>
            </div>
            {auditLoading && <div className="admin-drawer-body"><Loader2 className="animate-spin" size={16} /> {isArabic ? 'جارٍ التحميل…' : 'Loading…'}</div>}
            {!auditLoading && auditItems.length === 0 && (
              <div className="admin-drawer-body" data-testid="admin-audit-empty">
                {isArabic ? 'لا توجد سجلات لهذا الإعلان.' : 'No audit entries for this ad.'}
              </div>
            )}
            {!auditLoading && auditItems.length > 0 && (
              <ul className="admin-drawer-body admin-audit-list">
                {auditItems.map((it, idx) => (
                  <li key={idx} className="admin-audit-item" data-testid={`admin-audit-item-${idx}`}>
                    <div className="admin-audit-row1">
                      <span className="admin-pill blue">{it.action}</span>
                      <span className="admin-audit-time">{it.timestamp ? new Date(it.timestamp).toLocaleString() : ''}</span>
                    </div>
                    <div className="admin-audit-row2">
                      <span className="admin-audit-actor">{it.actor_admin_id || '—'} · {it.actor_role || ''}</span>
                      <span>{it.old_value?.status || '—'} → {it.new_value?.status || '—'}</span>
                    </div>
                    {it.reason && <div className="admin-audit-reason">{it.reason}</div>}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
